import type { AuthCredentialFailureCategory } from '@shared/utils/analytics/events';

export type PasskeyEnrollmentErrorOutcome =
  | { lifecycleState: 'enrollment_cancelled' }
  | {
      lifecycleState: 'enrollment_failed';
      failureCategory: AuthCredentialFailureCategory;
    };

export type PasskeyEnrollmentErrorShape =
  'dom_exception' | 'error' | 'string' | 'record_detail' | 'record' | 'unknown';

export interface PasskeyEnrollmentErrorDiagnostics {
  errorName:
    | 'abort_error'
    | 'invalid_state_error'
    | 'network_error'
    | 'not_allowed_error'
    | 'not_supported_error'
    | 'operation_error'
    | 'security_error'
    | 'timeout_error'
    | 'unclassified';
  errorShape: PasskeyEnrollmentErrorShape;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getKnownErrorText = (error: unknown): string => {
  const values: unknown[] = [];

  if (error instanceof Error) {
    values.push(error.name, error.message);
  }

  if (typeof error === 'string') {
    values.push(error);
  }

  if (isRecord(error)) {
    values.push(
      error.name,
      error.type,
      error.error,
      error.text,
      error.message,
      error.description,
      error.errorDescription
    );

    const detail = error.detail;
    if (typeof detail === 'string') {
      values.push(detail);
    } else if (isRecord(detail)) {
      values.push(
        detail.name,
        detail.type,
        detail.error,
        detail.text,
        detail.message,
        detail.description,
        detail.errorDescription
      );
    }
  }

  return values
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
};

const NORMALISED_PASSKEY_ERROR_NAMES = {
  aborterror: 'abort_error',
  invalidstateerror: 'invalid_state_error',
  networkerror: 'network_error',
  notallowederror: 'not_allowed_error',
  notsupportederror: 'not_supported_error',
  operationerror: 'operation_error',
  securityerror: 'security_error',
  timeouterror: 'timeout_error',
} as const;

export const getPasskeyEnrollmentErrorDiagnostics = (
  error: unknown
): PasskeyEnrollmentErrorDiagnostics => {
  const detail = isRecord(error) ? error.detail : undefined;
  const errorShape: PasskeyEnrollmentErrorShape =
    typeof DOMException !== 'undefined' && error instanceof DOMException
      ? 'dom_exception'
      : error instanceof Error
        ? 'error'
        : typeof error === 'string'
          ? 'string'
          : isRecord(detail)
            ? 'record_detail'
            : isRecord(error)
              ? 'record'
              : 'unknown';
  const directName = isRecord(error) ? error.name : undefined;
  const detailName = isRecord(detail) ? detail.name : undefined;
  const rawName =
    typeof directName === 'string'
      ? directName
      : typeof detailName === 'string'
        ? detailName
        : error instanceof Error
          ? error.name
          : undefined;
  const normalizedName = rawName?.replace(/[^a-z]/giu, '').toLowerCase();

  return {
    errorName:
      normalizedName && normalizedName in NORMALISED_PASSKEY_ERROR_NAMES
        ? NORMALISED_PASSKEY_ERROR_NAMES[
            normalizedName as keyof typeof NORMALISED_PASSKEY_ERROR_NAMES
          ]
        : 'unclassified',
    errorShape,
  };
};

export function classifyPasskeyEnrollmentError(
  error: unknown
): PasskeyEnrollmentErrorOutcome {
  const errorText = getKnownErrorText(error);
  const isExplicitPermissionDenial =
    errorText.includes('permission denied') ||
    errorText.includes('permissions policy') ||
    errorText.includes('blocked by policy') ||
    errorText.includes('disallowed by policy') ||
    errorText.includes('not permitted') ||
    errorText.includes('feature is not enabled') ||
    errorText.includes('publickey-credentials') ||
    errorText.includes('same-origin with all of its ancestors');

  // DOMException names are locale-independent. English message heuristics only
  // enrich provider errors; unrecognised localised text safely degrades to unknown.
  if (
    errorText.includes('cancelled') ||
    errorText.includes('canceled') ||
    errorText.includes('aborterror') ||
    errorText.includes('aborted by the user') ||
    errorText.includes('user aborted') ||
    errorText.includes('dismissed') ||
    ((errorText.includes('notallowederror') ||
      errorText.includes('not allowed')) &&
      !isExplicitPermissionDenial)
  ) {
    return { lifecycleState: 'enrollment_cancelled' };
  }

  let failureCategory: AuthCredentialFailureCategory = 'unknown';

  if (
    errorText.includes('notsupportederror') ||
    errorText.includes('not supported') ||
    errorText.includes('unsupported')
  ) {
    failureCategory = 'not_supported';
  } else if (isExplicitPermissionDenial) {
    failureCategory = 'not_allowed';
  } else if (
    errorText.includes('invalidstateerror') ||
    errorText.includes('invalid state') ||
    errorText.includes('already registered')
  ) {
    failureCategory = 'invalid_state';
  } else if (
    errorText.includes('securityerror') ||
    errorText.includes('security error') ||
    errorText.includes('insecure')
  ) {
    failureCategory = 'security_error';
  } else if (
    errorText.includes('rate limit') ||
    errorText.includes('rate-limit') ||
    errorText.includes('ratelimit') ||
    errorText.includes('too many requests') ||
    errorText.includes('429') ||
    errorText.includes('throttl')
  ) {
    failureCategory = 'rate_limited';
  } else if (
    errorText.includes('provider') ||
    errorText.includes('service unavailable') ||
    errorText.includes('internal server')
  ) {
    failureCategory = 'provider_error';
  }

  return {
    lifecycleState: 'enrollment_failed',
    failureCategory,
  };
}
